import { Injectable } from '@angular/core';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import {
    CancelEditMode,
    DeleteFiling,
    ResetState,
    SetEditMode,
    SetObjectState,
    UpdateDocument
} from '@Core/models/proprietary/document-detail/document-detail-object.actions';
import { AppService } from '@Core/services/proprietary/app.service';
import { StateHelperService } from '@Core/services/proprietary/state-helper.service';
import { cloneDeep } from 'src/ep-utils';

export class DocumentDetailFilingType {
    Edit_Status: string;
    DocLink_ID: string;
    Entity?: { Entity_ID: string; Role_Type: string; Entity_Name: string };
    Contract?: { Contract_ID: string; Contract_Num: string; Eff_Date: string };
    Claim?: { Claim_ID: string; Book_Claim_Num: string };
    File_Drawer?: string;
    File_Drawer_Desc?: string;
    File_Folder?: string;
    File_Folder_Desc?: string;
    Public_Document_Ind?: string;
    Common_Doc_Ind?: string;
    Filing_Date?: string;
}

export class DocumentDetailType {
    Doc_ID: string;
    Doc_Name?: string;
    Doc_Desc?: string;
    Doc_Class?: string;
    Doc_Class_Desc?: string;
    Protected_Ind?: string;
    Disbled_Ind?: string;
    Ext_Collation_Seq?: string;
    Sent_Ind?: string;
    Filed_Ind?: string;
    Doc_Status?: string;
    Created_By?: { Entity_ID?: ''; Entity_Name?: ''; Date?: '' };
    Last_Revised_By?: { Entity_ID?: ''; Entity_Name?: ''; Date?: '' };
    Filing_User?: { Entity_ID?: ''; Entity_Name?: '' };
    Filing_Workgroup?: { Workgroup_ID?: ''; Workgroup_Name?: '' };
    Filing?: DocumentDetailFilingType[];
}

export interface DocumentDetailObjectStateModel {
    currentDocID: string;
    entities: any;
    document: any;
    filing: any;
    origDocument: any;
    initialProtectedInd: string;
}

const DocumentDetailObjectStateDefaults: DocumentDetailObjectStateModel = {
    currentDocID: null,
    entities: {},
    document: {},
    filing: {},
    origDocument: {},
    initialProtectedInd: null
};

@State<DocumentDetailObjectStateModel>({
    name: 'documentDetailObjectState',
    defaults: DocumentDetailObjectStateDefaults
})
@Injectable()
export class DocumentDetailObjectState {
    constructor(private app: AppService, private helper: StateHelperService) { }

    @Selector()
    static entities(state: DocumentDetailObjectStateModel) {
        let tempEntities = { ...state.entities };
        tempEntities.Document = state.document;
        tempEntities.Filing = state.filing;
        return tempEntities;
    }

    @Selector()
    static document(state: DocumentDetailObjectStateModel) {
        return state.document[state.currentDocID] || null;
    }

    @Selector()
    static origDocument(state: DocumentDetailObjectStateModel) {
        return state.origDocument || null;
    }

    @Selector()
    static initialDocumentProtectedInd(state: DocumentDetailObjectStateModel) {
        return state.initialProtectedInd || null;
    }

    @Selector()
    static filings(state: DocumentDetailObjectStateModel) {
        let result = [];

        let hasFilings = Object.keys(state.filing).length > 0;
        if (hasFilings) {
            for (var i in state.filing) {
                result.push(state.filing[i]);
            }
        }

        if (result.length > 0) {
            return result;
        } else {
            return null;
        }
    }

    @Selector()
    static currentDocID(state: DocumentDetailObjectStateModel) {
        if (state.currentDocID) {
            return state.currentDocID;
        } else {
            return null;
        }
    }

    @Action(SetObjectState)
    setObjectState(sc: StateContext<DocumentDetailObjectStateModel>, action: SetObjectState) {
        const state = sc.getState();

        let docID = null;
        for (var i in action.entities.Document) {
            docID = i;
            break;
        }

        let entities = { ...action.entities };
        delete entities.Document;

        let document = cloneDeep(action.entities.Document);
        let initProtectInd;

        for (var j in document) {
            let docStatus = document[j].Doc_Status;
            if (docStatus == 'DIS') {
                document[j].Disabled_Ind = 'Y';
            } else {
                document[j].Disabled_Ind = 'N';
            }

            if (!document[j].Doc_Desc) {
                document[j].Doc_Desc = '';
            }

            if (!document[j].Ext_Collation_Seq) {
                document[j].Ext_Collation_Seq = '';
            }

            initProtectInd = document[j].Protected_Ind;
        }

        let filing = action.entities.Filing ? { ...action.entities.Filing } : {};

        sc.patchState({
            entities: entities,
            document: document,
            currentDocID: docID,
            filing: filing,
            initialProtectedInd: initProtectInd
        });
    }

    @Action(ResetState)
    resetState(sc: StateContext<DocumentDetailObjectStateModel>, action: ResetState) {
        const state = sc.getState();

        sc.setState({
            currentDocID: null,
            entities: {},
            document: {},
            filing: {},
            origDocument: {},
            initialProtectedInd: null
        });
    }

    @Action(UpdateDocument)
    updateDocument(sc: StateContext<DocumentDetailObjectStateModel>, action: UpdateDocument) {
        const state = sc.getState();

        const docID = action.document.Doc_ID;
        if (!docID) {
            return;
        }
        const currentDoc = state.document[docID];
        let updateDoc: any = { ...action.document };
        let currentEditStatus = currentDoc.Edit_Status;
        if (!currentEditStatus && docID) {
            currentEditStatus = 'N';
        }
        this.helper.setItemChanged(updateDoc, currentEditStatus);

        sc.patchState({
            document: {
                ...state.document,
                [docID]: { ...currentDoc, ...updateDoc }
            }
        });
    }

    @Action(DeleteFiling)
    deleteFiling(sc: StateContext<DocumentDetailObjectStateModel>, action: DeleteFiling) {
        const state = sc.getState();

        let document = { ...state.document[state.currentDocID] };
        if (!document.Filing) {
            document.Filing = [];
        }

        let filing = cloneDeep(state.filing);

        for (var i in filing) {
            if (filing[i].DocLink_ID == action.docLinkID) {
                document.Filing = this.helper.setItemDeleted(filing, action.docLinkID);
            }
        }

        sc.patchState({
            filing: filing,
            document: { ...state.document, [state.currentDocID]: document }
        });
    }

    @Action(SetEditMode)
    setEditMode(sc: StateContext<DocumentDetailObjectStateModel>, action: SetEditMode) {
        const state = sc.getState();

        let document = cloneDeep({ ...state.document[state.currentDocID] });

        sc.patchState({
            origDocument: {
                ...document
            }
        });
    }

    @Action(CancelEditMode)
    cancelEditMode(sc: StateContext<DocumentDetailObjectStateModel>, action: CancelEditMode) {
        const state = sc.getState();

        const docID = state.currentDocID;
        let document = cloneDeep({ ...state.origDocument });

        let filing;

        if (state.filing && Object.keys(state.filing).length > 0) {
            filing = cloneDeep({ ...state.filing });
            for (var i in filing) {
                if (filing[i].Edit_Status == 'D') {
                    filing[i].Edit_Status = 'N';
                }
            }
        } else {
            filing = {};
        }

        sc.patchState({
            document: {
                ...state.document,
                [docID]: { ...document }
            },
            filing: filing
        });
    }
}
